import type { AnchorHTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface NavLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  active?: boolean;
  children: ReactNode;
}

/** Top-navigation item. Active is bold ngahere on a ngahere tint. */
export function NavLink({ active = false, className, children, ...rest }: NavLinkProps) {
  return (
    <a
      aria-current={active ? 'page' : undefined}
      className={cn(
        'cursor-pointer rounded-md px-[13px] py-2 text-body no-underline',
        'transition-colors duration-fast ease-standard',
        active ? 'bg-ngahere-tint font-bold text-ngahere' : 'font-medium text-ink-2 hover:bg-hover',
        className,
      )}
      {...rest}
    >
      {children}
    </a>
  );
}

export interface TabBarItem {
  key: string;
  label: string;
  icon: ReactNode;
  badge?: boolean;
}

export interface TabBarProps {
  items: TabBarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  className?: string;
}

/**
 * Mobile bottom tab bar. The design export is desktop-only, so this follows the
 * native pattern the brief specifies while drawing entirely on tokens already
 * present in the export — see docs/design-fidelity.md and ADR-0010.
 *
 * `h-tab-bar` is what makes the height shared rather than incidental: the
 * search screen's results sheet and a ride's sticky booking bar both sit
 * directly on top of this element and clear it with `above-tab-bar`.
 */
export function TabBar({ items, activeKey, onSelect, className }: TabBarProps) {
  return (
    <nav
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 bg-surface/95 backdrop-blur-md pb-safe',
        className,
      )}
      aria-label="Main"
    >
      {/* The hairline is on the list, not on the `nav`, so that it falls inside
          `h-tab-bar` rather than adding a pixel on top of it. `above-tab-bar`
          clears exactly that height; a border outside it left the ride screen's
          booking bar one pixel underneath this one. */}
      <ul className="flex h-tab-bar border-t border-line">
        {items.map((item) => {
          const active = item.key === activeKey;
          return (
            <li key={item.key} className="flex-1">
              <button
                type="button"
                onClick={() => onSelect(item.key)}
                aria-current={active ? 'page' : undefined}
                className={cn(
                  'relative flex h-full w-full cursor-pointer flex-col items-center justify-center gap-1',
                  'text-tiny font-semibold transition-colors duration-fast ease-standard',
                  active ? 'text-ngahere' : 'text-subtle',
                )}
              >
                {/* Decorative: every tab has its label right underneath, so an
                    icon that carries its own name — SmolitMark does — would
                    have the button announced as "Smolit Smolit". */}
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
                {item.badge ? (
                  <span className="absolute top-1.5 right-[calc(50%-16px)] h-[7px] w-[7px] rounded-full border-[1.5px] border-white bg-pohutukawa" />
                ) : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
